import React from 'react';
import PropTypes from 'prop-types';
import Animated from 'animated';
import Easing from 'animated/lib/Easing';

const AnimatedRect = Animated.createAnimatedComponent('rect');
const AnimatedImage = Animated.createAnimatedComponent('image');
const AnimatedG = Animated.createAnimatedComponent('g');
const AnimatedText = Animated.createAnimatedComponent('text');

export default class AnimatedLetterMask extends React.PureComponent {
    static propTypes = {
        clipId: PropTypes.string.isRequired,
        maskWidth: PropTypes.number.isRequired,
        maskHeight: PropTypes.number.isRequired,
        imageWidth: PropTypes.number.isRequired,
        imageHeight: PropTypes.number.isRequired,
        startX: PropTypes.number.isRequired,
        startY: PropTypes.number.isRequired,
        backgroundX: PropTypes.number.isRequired,
        backgroundY: PropTypes.number.isRequired,
        backgroundScale: PropTypes.number.isRequired,
        image: PropTypes.string.isRequired,
        animationConfig: PropTypes.object.isRequired,
        onAnimationStarted: PropTypes.func.isRequired,
        onAnimationEnded: PropTypes.func.isRequired,
        active: PropTypes.bool,
        debug: PropTypes.bool,
        children: PropTypes.node.isRequired
    }

    static defaultProps = {
        animationConfig: {},
        backgroundX: 0,
        backgroundY: 0,
        backgroundScale: 1,
        debug: false
    };

    state = {
        animation: new Animated.Value(0)
    };

    mouseIn = () => {
        let ok = this.props.onAnimationStarted(this.props.clipId);
        if (ok === false) {
            return;
        }

        Animated.timing(
            this.state.animation, {
                toValue: 1, 
                duration: 3000
            }
        ).start(({finished}) => {
            this.props.onAnimationEnded(this.props.clipId, finished);
        });
    };
    mouseOut = () => {
        if (this._animationValue <= 0.75) {
            this.stopAnimation();
        }
    }

    stopAnimation = () => {
        this.state.animation.stopAnimation((value) => {
            Animated.timing(this.state.animation, {toValue: 0, duration: 3000 * value}).start();
        });
    }

    componentWillMount() {
        this.state.animation.addListener((value) => this._animationValue = value.value);
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.active && !nextProps.active) {
            this.stopAnimation();
        }
    }

    buildRepositionTransform() {
        let startX = this.props.startX;
        let startY = this.props.startY;

        let translateX = this.state.animation.interpolate({
            inputRange: [0, 1],
            outputRange: [startX, startX]
        });
        let translateY = this.state.animation.interpolate({
            inputRange: [0, 1],
            outputRange: [startY, startY]
        });

        return Animated.template`translate(${translateX}, ${translateY})`;
    }

    buildScaleMatrix(interpolationConfig, xCenter, yCenter) {
        let easing = Easing.in(Easing.quad);

        let scale = this.state.animation.interpolate({
            ...interpolationConfig,
            easing
        });

        let matrixE = this.state.animation.interpolate({
            inputRange: interpolationConfig.inputRange,
            outputRange: interpolationConfig.outputRange.map((scale) => xCenter - scale * xCenter),
            easing
        });

        let matrixF = this.state.animation.interpolate({
            inputRange: interpolationConfig.inputRange,
            outputRange: interpolationConfig.outputRange.map((scale) => yCenter - scale * yCenter),
            easing
        });

        return Animated.template`matrix(${scale}, 0, 0, ${scale}, ${matrixE}, ${matrixF})`;
    }

    buildScaleAnimation() {
        if (!this.props.animationConfig.scale) {
            return '';
        }

        let xCenter = this.props.maskWidth / 2;
        let yCenter = this.props.maskHeight / 2;

        return this.buildScaleMatrix(this.props.animationConfig.scale, xCenter, yCenter);
    }

    buildTranslateAnimation() {
        let config = this.props.animationConfig;

        let translateX = 0;
        if (config.translateX) {
            translateX = this.state.animation.interpolate(config.translateX);
        }

        let translateY = 0;
        if (config.translateY) {
            translateY = this.state.animation.interpolate(config.translateY);
        }

        return Animated.template`translate(${translateX}, ${translateY})`;
    }

    buildRotationAnimation() {
        let config = this.props.animationConfig;

        if (!config.rotation) {
            return '';
        }

        let xCenter = this.props.maskWidth / 2;
        let yCenter = this.props.maskHeight / 2;

        let rotation = this.state.animation.interpolate(config.rotation);

        return Animated.template`rotate(${rotation}, ${xCenter}, ${yCenter})`;
    }

    buildBackgroundShiftTransform() {
        let xCenter = this.props.maskWidth / 2 + this.props.startX;
        let yCenter = this.props.maskHeight / 2 + this.props.startY;
        let easing = Easing.in(Easing.quad);

        let interpolationConfig = {
            inputRange: [0, 1],
            outputRange: [this.props.backgroundScale, this.props.backgroundScale],
            easing
        };

        let scaleTransform = this.buildScaleMatrix(interpolationConfig, xCenter, yCenter);

        // let xShift = this.state.animation.interpolate({
        //     inputRange: [0, 0.001, 1],
        //     outputRange: [this.props.backgroundX, 0, 0],
        //     easing
        // });

        // let yShift = this.state.animation.interpolate({
        //     inputRange: [0, 0.001, 1],
        //     outputRange: [this.props.backgroundY, 0, 1],
        //     easing
        // });

        return Animated.template`translate(${this.props.backgroundX}, ${this.props.backgroundY}) ${scaleTransform}`;
    }

    renderProgressCounter(transform) {
        if (this.props.debug) {
            return <AnimatedText fill="white" x="0" y="220" transform={transform}>{this.state.animation}</AnimatedText>;
        }
    }

    render() {
        let repositionTransform = this.buildRepositionTransform();
        let scaleTransform = this.buildScaleAnimation();
        let translateTransform = this.buildTranslateAnimation();
        let rotationTransform = this.buildRotationAnimation();
        let transform = Animated.template`${repositionTransform} ${scaleTransform} ${translateTransform} ${rotationTransform}`;

        let backgroundShift = this.buildBackgroundShiftTransform();
        let backgroundSwitchOpacity = this.state.animation.interpolate({
            inputRange: [0, 0.05, 1],
            outputRange: [1, 0, 0]
        });

        let clipId = `letter-${this.props.clipId}`;

        return (
            <g>
                <defs> 
                    <clipPath id={clipId}>
                        {React.Children.map(this.props.children, (child) => (
                            React.cloneElement(child, {transform})
                        ))} 
                    </clipPath>
                </defs>
                <AnimatedG clipPath={`url(#${clipId})`}>
                    <AnimatedImage
                        className="svg-image" xlinkHref={this.props.image} 
                        width={this.props.imageWidth} height={this.props.imageHeight}/> 
                    <AnimatedImage
                        className="svg-image-shift" xlinkHref={this.props.image} 
                        width={this.props.imageWidth} height={this.props.imageHeight}
                        transform={backgroundShift} opacity={backgroundSwitchOpacity}/> 
                </AnimatedG>
                <g>
                    <AnimatedRect
                        className="hitbox" 
                        width={this.props.maskWidth} height={this.props.maskHeight}
                        onMouseEnter={this.mouseIn} onMouseLeave={this.mouseOut}
                        onClick={this.mouseIn}
                        transform={transform} />
                    {this.renderProgressCounter(repositionTransform)}
                </g>
            </g>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/animated-letter-mask.js